import React, {CSSProperties, useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {getCurrencyValue, parseDate} from 'utils'
import {useTranslation} from 'i18n'
import {Icon, IconFirstBuy, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {FontColor, IconType} from 'types'
import {
  GIFT_SHOP_TRANSACTION_STATUS_COLORS,
  GIFT_SHOP_TRANSACTION_STATUS_ICONS,
} from 'consts'
import {TemplateTransactionHistoryContentItemCommonProps} from './TemplateTransactionHistoryContentItemCommonProps'

const StyledCardContainer = styled.div`
  background-color: ${({theme}) => theme.white_1};
  box-sizing: border-box;
  border-radius: ${convertUnit(8)};
  padding: ${convertUnit(20)};
  display: flex;
  align-items: stretch;
  flex-direction: row;
  min-height: ${convertUnit(107)};
  border-bottom: ${convertUnit(1)} solid ${({theme}) => theme.white_4};
  cursor: pointer;
`

const StyledInfo = styled.div<CSSProperties>`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: ${({justifyContent}) => justifyContent};
`

const StyledTypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(8)};
  align-items: center;
`

const StyledTransactionContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const StyledTransactionContentDataRow = styled.div<CSSProperties>`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  flex: 1;
  margin-top: ${({marginTop}) => marginTop};
`

const StyledMultipleStatusContainer = styled.div`
  display: flex;
  flex: 1;
  width: fit-content;
`

const StyledFlexContainer = styled.div<CSSProperties>`
  display: flex;
  flex: ${({flex}) => flex};
  margin-top: ${({marginTop}) => marginTop};
  margin-left: ${({marginLeft}) => marginLeft};
  margin-right: ${({marginRight}) => marginRight};
  align-items: center;
`

export default function TemplateTransactionHistoryContentItemCommon({
  data,
  onClickItem,
}: TemplateTransactionHistoryContentItemCommonProps) {
  const {
    transaction_date: date,
    transaction_type: type,
    amount,
    is_free_first_buy,
    transaction_partner,
    count_completed_invoices,
    count_waiting_hires_invoices,
    status,
  } = data
  const {translate} = useTranslation()

  const handleTransactionType = useMemo(
    () =>
      type && (
        <StyledTypeContainer>
          <Paragraph fontWeight="bold" fontSize="m">
            {translate('giftShop:transactionType', {
              context: type.toUpperCase(),
            })}
          </Paragraph>
        </StyledTypeContainer>
      ),
    [translate, type],
  )

  const handleTransactionPartner = useMemo(
    () => (
      <StyledInfo justifyContent="end">
        <Paragraph
          fontSize="s"
          lineHeight={17}
          fontWeight="bold"
          color="gray_5">
          {transaction_partner.length > 15
            ? `${transaction_partner.slice(0, 15)}...`
            : transaction_partner}
        </Paragraph>
      </StyledInfo>
    ),
    [transaction_partner],
  )

  const handleAmount = useMemo(
    () => (
      <StyledInfo justifyContent="start">
        <Paragraph fontSize="m">{getCurrencyValue(amount)}</Paragraph>
      </StyledInfo>
    ),
    [amount],
  )

  const handleDate = useMemo(
    () => (
      <StyledInfo justifyContent="end">
        <Paragraph fontSize="s" color="gray_4">
          {parseDate(date, 'MMM D, YYYY')}
        </Paragraph>
      </StyledInfo>
    ),
    [date],
  )

  const handleRenderSingleStatus = useCallback(
    (text: string, icon: IconType, color: FontColor) => (
      <StyledFlexContainer marginLeft={convertUnit(-4)}>
        <Icon type={icon} color={color} />
        <Paragraph fontSize="s" fontWeight="medium" color={color}>
          {text}
        </Paragraph>
      </StyledFlexContainer>
    ),
    [],
  )

  const handleTransactionStatus = useMemo(() => {
    if (!status) return <></>

    if (count_completed_invoices && count_waiting_hires_invoices) {
      return (
        <StyledMultipleStatusContainer>
          {handleRenderSingleStatus(
            `${count_waiting_hires_invoices} ${translate(
              'giftShop:transactionStatus',
              {
                context: 'WAITING_HIRES',
              },
            )}`,
            GIFT_SHOP_TRANSACTION_STATUS_ICONS.WAITING_HIRES,
            GIFT_SHOP_TRANSACTION_STATUS_COLORS.WAITING_HIRES,
          )}
          <StyledFlexContainer
            marginLeft={convertUnit(4)}
            marginRight={convertUnit(4)}>
            <Paragraph>•</Paragraph>
          </StyledFlexContainer>
          {handleRenderSingleStatus(
            `${count_completed_invoices} ${translate(
              'giftShop:transactionStatus',
              {
                context: 'COMPLETED',
              },
            )}`,
            GIFT_SHOP_TRANSACTION_STATUS_ICONS.COMPLETED,
            GIFT_SHOP_TRANSACTION_STATUS_COLORS.COMPLETED,
          )}
        </StyledMultipleStatusContainer>
      )
    }

    return handleRenderSingleStatus(
      type === 'Withdraw' && status === 'PROCESSED'
        ? translate('global:processed')
        : translate('giftShop:transactionStatus', {context: status}),
      GIFT_SHOP_TRANSACTION_STATUS_ICONS[status],
      GIFT_SHOP_TRANSACTION_STATUS_COLORS[status],
    )
  }, [
    count_completed_invoices,
    count_waiting_hires_invoices,
    handleRenderSingleStatus,
    status,
    translate,
    type,
  ])

  return (
    <StyledCardContainer onClick={onClickItem}>
      <StyledTransactionContentContainer>
        <StyledTransactionContentDataRow>
          {handleTransactionStatus}
          {handleDate}
        </StyledTransactionContentDataRow>
        <StyledTransactionContentDataRow marginTop={convertUnit(2)}>
          {handleTransactionType}
          {handleAmount}
        </StyledTransactionContentDataRow>
        <StyledTransactionContentDataRow marginTop={convertUnit(2)}>
          {handleTransactionPartner}
          {is_free_first_buy ? (
            <IconFirstBuy text={translate('giftShop:firstPromo')} />
          ) : (
            <></>
          )}
        </StyledTransactionContentDataRow>
      </StyledTransactionContentContainer>
    </StyledCardContainer>
  )
}
